import { memo, useCallback, useEffect, useMemo } from "react";

import {
    Column,
    Icon,
    InputWrapper,
    Row,
    Text,
    TextButton,
    TextSkeleton,
    TooltipText,
    useAppPalette,
    useMemoizedKeyMap
} from "@bridgesplit/ui";
import {
    formatTokenMetadata,
    usePresetMarketInfos,
    useSupportedCollateral,
    deduplicateCollateralOraclePairs,
    MarketAssetView,
    ParsedMarketInformationWithLtvInfo,
    StrategyExpandedInfo
} from "@bridgesplit/abf-react";
import { filterUndefined } from "@bridgesplit/utils";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import { AppDialog } from "app/utils";
import { COPY } from "app/constants";

import { useMarketContext, useQuotesContext } from "../common";
import { useMarketLendContext } from "./MarketLendContext";
import { OverlappingMetadataImages } from "../../common";
import { useMarketCollateral, useMarketDialog } from "../util";

export default memo(function LendCollateralSelect({
    isStat,
    existingStrategy
}: {
    isStat?: boolean;
    existingStrategy?: StrategyExpandedInfo;
}) {
    const { form } = useMarketLendContext();
    const { principalMint } = useMarketContext();

    const tokens = useMarketCollateral();
    const getKey = useCallback((t: TokenListMetadata) => t.mint, []);
    const tokensByMint = useMemoizedKeyMap(tokens, getKey);

    // Fetch verified market infos once at the top level
    const {
        data: verifiedMarketInfos,
        getDefaultMarketInfoForPrincipal,
        isLoading: marketInfosLoading,
        getMarketByCollateralAndPrincipalMint
    } = usePresetMarketInfos({
        principalMints: principalMint ? [principalMint] : [],
        addresses: []
    });

    // Deduplicate collateral pairs - picks higher LTV if same collateral in multiple market infos
    const deduplicatedResult = useMemo(() => {
        if (!verifiedMarketInfos) return undefined;
        return deduplicateCollateralOraclePairs(verifiedMarketInfos);
    }, [verifiedMarketInfos]);

    // Show loading state only while market infos are loading
    // Once loaded, show the actual state (even if 0 tokens)
    const isLoading = marketInfosLoading || !principalMint || !tokens || !verifiedMarketInfos;

    // iterate through form.collateral instead of tokens to preserve selection order
    const selectedTokens: TokenListMetadata[] | undefined = form.collateral
        .map((c) => tokensByMint?.get(c))
        .filter(filterUndefined);

    if (isStat) {
        return (
            <Row spaceBetween>
                <TooltipText helpText={COPY.ALLOWED_COLLATERAL_TOOLTIP} color="caption">
                    {COPY.ALLOWED_COLLATERAL_TERM}
                </TooltipText>
                <StatSelect
                    selectedTokens={selectedTokens}
                    isLoading={isLoading}
                    verifiedMarketInfos={verifiedMarketInfos}
                    getDefaultMarketInfoForPrincipal={getDefaultMarketInfoForPrincipal}
                    getMarketByCollateralAndPrincipalMint={getMarketByCollateralAndPrincipalMint}
                    marketInfosLoading={isLoading}
                    deduplicatedResult={deduplicatedResult}
                />
            </Row>
        );
    }

    return (
        <Column spacing={0.5}>
            <TooltipText helpText={COPY.ALLOWED_COLLATERAL_TOOLTIP} variant="body2">
                {COPY.ALLOWED_COLLATERAL_TERM}
            </TooltipText>
            <CustomSelection
                selectedTokens={selectedTokens}
                isLoading={isLoading}
                verifiedMarketInfos={verifiedMarketInfos}
                getDefaultMarketInfoForPrincipal={getDefaultMarketInfoForPrincipal}
                getMarketByCollateralAndPrincipalMint={getMarketByCollateralAndPrincipalMint}
                marketInfosLoading={isLoading}
                deduplicatedResult={deduplicatedResult}
                existingStrategy={existingStrategy}
            />
        </Column>
    );
});

function StatSelect({
    selectedTokens,
    isLoading,
    verifiedMarketInfos,
    getDefaultMarketInfoForPrincipal,
    getMarketByCollateralAndPrincipalMint,
    marketInfosLoading,
    deduplicatedResult
}: {
    selectedTokens: TokenListMetadata[] | undefined;
    isLoading: boolean;
    verifiedMarketInfos: ParsedMarketInformationWithLtvInfo[] | undefined;
    getDefaultMarketInfoForPrincipal: (
        principalMint: string | undefined
    ) => ParsedMarketInformationWithLtvInfo | undefined;
    getMarketByCollateralAndPrincipalMint: (
        collateralMint: string | undefined,
        principalMint: string | undefined,
        marketInfoAddress?: string | undefined
    ) => MarketAssetView | undefined;
    marketInfosLoading: boolean;
    deduplicatedResult: ReturnType<typeof deduplicateCollateralOraclePairs> | undefined;
}) {
    const onClick = useSelectCollateral({
        verifiedMarketInfos,
        getDefaultMarketInfoForPrincipal,
        getMarketByCollateralAndPrincipalMint,
        marketInfosLoading,
        deduplicatedResult
    });

    return (
        <Row spacing={1}>
            {(isLoading || !selectedTokens || !verifiedMarketInfos || marketInfosLoading) && (
                <TextSkeleton variant="body1" />
            )}
            {!isLoading && !!selectedTokens?.length && (
                <OverlappingMetadataImages maxLength={3} size="20px" metadata={selectedTokens} />
            )}
            {!isLoading && selectedTokens?.length === 0 && <Text color="disabled">None</Text>}
            <TextButton onClick={onClick} color="caption" variant="body2">
                <Icon type="edit" />
            </TextButton>
        </Row>
    );
}

function CustomSelection({
    selectedTokens,
    isLoading,
    verifiedMarketInfos,
    getDefaultMarketInfoForPrincipal,
    getMarketByCollateralAndPrincipalMint,
    marketInfosLoading,
    deduplicatedResult,
    existingStrategy
}: {
    selectedTokens: TokenListMetadata[] | undefined;
    isLoading: boolean;
    verifiedMarketInfos: ParsedMarketInformationWithLtvInfo[] | undefined;
    getDefaultMarketInfoForPrincipal: (
        principalMint: string | undefined
    ) => ParsedMarketInformationWithLtvInfo | undefined;
    getMarketByCollateralAndPrincipalMint: (
        collateralMint: string | undefined,
        principalMint: string | undefined,
        marketInfoAddress?: string | undefined
    ) => MarketAssetView | undefined;
    marketInfosLoading: boolean;
    deduplicatedResult: ReturnType<typeof deduplicateCollateralOraclePairs> | undefined;
    existingStrategy?: StrategyExpandedInfo;
}) {
    const { hoverBackground } = useAppPalette();

    const onClick = useSelectCollateral({
        verifiedMarketInfos,
        getDefaultMarketInfoForPrincipal,
        getMarketByCollateralAndPrincipalMint,
        marketInfosLoading,
        deduplicatedResult,
        existingStrategy
    });

    const showSkeleton = isLoading || !selectedTokens || !verifiedMarketInfos || marketInfosLoading;

    return (
        <InputWrapper
            onClick={onClick}
            sx={{ justifyContent: "space-between", cursor: "pointer", ":hover": { background: hoverBackground } }}
        >
            {showSkeleton && <TextSkeleton variant="body1" />}
            {!showSkeleton && !!selectedTokens?.length && (
                <Row spacing={1}>
                    <OverlappingMetadataImages
                        disableTooltip
                        maxLength={3}
                        showPlus={false}
                        size="20px"
                        metadata={selectedTokens}
                    />
                    <Text>{formatTokenMetadata(selectedTokens, "tokens")}</Text>
                </Row>
            )}
            {!showSkeleton && selectedTokens?.length === 0 && <Text color="disabled">None</Text>}
            <TextButton color="caption" variant="body2">
                <Icon type="edit" />
            </TextButton>
        </InputWrapper>
    );
}

function useSelectCollateral({
    verifiedMarketInfos,
    getDefaultMarketInfoForPrincipal,
    getMarketByCollateralAndPrincipalMint,
    marketInfosLoading,
    deduplicatedResult,
    existingStrategy
}: {
    verifiedMarketInfos: ParsedMarketInformationWithLtvInfo[] | undefined;
    getDefaultMarketInfoForPrincipal: (
        principalMint: string | undefined
    ) => ParsedMarketInformationWithLtvInfo | undefined;
    getMarketByCollateralAndPrincipalMint: (
        collateralMint: string | undefined,
        principalMint: string | undefined,
        marketInfoAddress?: string | undefined
    ) => MarketAssetView | undefined;
    marketInfosLoading: boolean;
    deduplicatedResult: ReturnType<typeof deduplicateCollateralOraclePairs> | undefined;
    existingStrategy?: StrategyExpandedInfo;
}) {
    const { principalMint } = useMarketContext();

    const { form, setForm } = useMarketLendContext();

    const { setCollateralMints: setQuoteCollateral } = useQuotesContext();

    // Get ALL supported collateral across all market infos
    const allTokens = useSupportedCollateral(principalMint, {
        includePrincipalMint: false
    });

    const { open } = useMarketDialog();

    const selectedMarketInfo = getDefaultMarketInfoForPrincipal(principalMint);

    // Get collateral mints from the selected (default) market info for auto-selection
    const supportedCollateralMints = useMemo(() => {
        if (marketInfosLoading || !selectedMarketInfo) return undefined;

        // Get collateral from the default market info
        const collateralMints = selectedMarketInfo.marketInformationAssetInfo.map((asset) => asset.assetIdentifier);
        return collateralMints;
    }, [marketInfosLoading, selectedMarketInfo]);

    // Auto-select all tokens from the default market info on initial load
    // IMPORTANT: Only run this when market infos have fully loaded
    useEffect(() => {
        // A strategy is locked to its own market, so the default market's assets don't apply
        if (existingStrategy) return;

        // If principal changed and form wasn't initialized for this principal, reset collateral
        if (principalMint && form.collateralInitializedForMint && form.collateralInitializedForMint !== principalMint) {
            setQuoteCollateral([]);
            setForm((prev) => ({
                ...prev,
                collateral: [],
                collateralInitializedForMint: null,
                selectedMarketInfoAddress: undefined
            }));
            return;
        }

        // Auto-select if not yet initialized for this principal
        if (
            !marketInfosLoading &&
            supportedCollateralMints &&
            supportedCollateralMints.length > 0 &&
            (!form.collateralInitializedForMint || form.collateralInitializedForMint !== principalMint)
        ) {
            setQuoteCollateral(supportedCollateralMints);
            setForm((prev) => ({
                ...prev,
                collateral: supportedCollateralMints,
                collateralInitializedForMint: principalMint || null,
                selectedMarketInfoAddress: selectedMarketInfo?.marketInformation.address
            }));
        }
    }, [
        marketInfosLoading,
        supportedCollateralMints,
        form.collateralInitializedForMint,
        principalMint,
        setForm,
        setQuoteCollateral,
        selectedMarketInfo,
        existingStrategy
    ]);
    return useCallback(
        () =>
            open(AppDialog.MultiSelectCollateral, {
                tokens: allTokens,
                principalMint,
                isAdvanced: true,
                collateral: form.collateral,
                ...(existingStrategy
                    ? {
                          existingStrategy,
                          selectedMarketInfoAddress: existingStrategy.strategy.marketInformation
                      }
                    : {
                          selectedMarketInfoAddress:
                              form.selectedMarketInfoAddress || selectedMarketInfo?.marketInformation.address,
                          verifiedMarketInfos,
                          collateralToMarketInfoOptions: deduplicatedResult?.collateralToMarketInfoOptions
                      }),
                setCollateral: (collateral, selectedMarketInfoAddress) => {
                    const collateralMints = collateral.map((c) => c.metadata.mint);
                    setQuoteCollateral(collateralMints);
                    setForm((prev) => ({
                        ...prev,
                        collateral: collateralMints,
                        selectedMarketInfoAddress: selectedMarketInfoAddress || prev.selectedMarketInfoAddress
                    }));
                }
            }),
        [
            form.collateral,
            form.selectedMarketInfoAddress,
            open,
            principalMint,
            setForm,
            setQuoteCollateral,
            allTokens,
            verifiedMarketInfos,
            selectedMarketInfo,
            deduplicatedResult,
            existingStrategy
        ]
    );
}
